import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { differenceInCalendarDays } from "date-fns";
import { ChevronLeft, Clock, MessageSquare } from "lucide-react";
import Avatar from "../../components/common/Avatar";
import FifaVerifiedBadge, {
  isFifaVerified,
} from "../../components/common/FifaVerifiedBadge";
import ProposalCard from "../../components/club-network/ProposalCard";
import { trustedAgentService } from "../../services/clubNetworkApi";
import { formatAbsoluteDate } from "../../utils/searchRequestFormat";

// A2 / A3 — the commissioning club's READ-ONLY delivery view (opens from "View
// proposals"). Header (N proposals from [agent], delivered date, engagement
// line, chat chip + Extend), the agent's summary note above the proposals, then
// the A6·b proposal cards — two-up from 900px (Brief line 54). An empty delivery
// (A3) shows the agent's reason instead of cards.
//
// The player data on every card is hydrated from the record by the endpoint —
// this view never free-types a player field (Annex Z.7).

const DELIVERY_TIMER_DAYS = 5;

// The commission chat's countdown returns after delivery: 5 days from the
// delivered moment. Renders "expires in N days" / "expires today" / "expired".
const chatExpiryLabel = (deliveredAt) => {
  if (!deliveredAt) return null;
  const delivered = new Date(deliveredAt);
  if (Number.isNaN(delivered.getTime())) return null;
  const expiry = new Date(delivered.getTime());
  expiry.setDate(expiry.getDate() + DELIVERY_TIMER_DAYS);
  const days = differenceInCalendarDays(expiry, new Date());
  if (days < 0) return "Chat expired";
  if (days === 0) return "Chat expires today";
  return `Chat expires in ${days} day${days === 1 ? "" : "s"}`;
};

// "Commissioned 12 May · searched 82 days" — the engagement anchors. Commissioned
// date from the request's creation; searched days from when the live request went
// up (searching_since) to the delivery. Each half renders only when derivable.
const engagementLine = (delivery, request) => {
  const bits = [];
  const commissioned = request?.created_at || delivery?.searching_since;
  if (commissioned) bits.push(`Commissioned ${formatAbsoluteDate(commissioned)}`);
  if (delivery?.searching_since && delivery?.delivered_at) {
    const days = differenceInCalendarDays(
      new Date(delivery.delivered_at),
      new Date(delivery.searching_since),
    );
    if (Number.isFinite(days) && days >= 0) {
      bits.push(`searched ${days} day${days === 1 ? "" : "s"}`);
    }
  }
  // Returns the bits (not a joined string) so each half — "Commissioned {date}"
  // and "searched {n} days" — reaches the DOM translation layer as its own text
  // node and matches its own bundle key (QCS-T9: a joined single node left the
  // whole line in English).
  return bits;
};

const ClubDeliveryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactingId, setContactingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      trustedAgentService.getDelivery(id),
      trustedAgentService.getRequest(id),
    ]).then(([del, req]) => {
      if (cancelled) return;
      if (del.success) setDelivery(del.data);
      else setError(del.error || "No delivery yet.");
      if (req.success) setRequest(req.data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // The commission chat channel — from the winning recipient (accepted through
  // delivered). Extend / Open chat route into the inbox where the timer lives.
  const chatChannelId = (() => {
    const rec = (request?.recipients || []).find((r) =>
      ["accepted", "searching", "delivered", "nothing_found"].includes(r.status),
    );
    return rec?.chat_channel_id || null;
  })();

  const openChat = () => {
    navigate(chatChannelId ? `/inbox?channel=${chatChannelId}` : "/inbox");
  };

  const contactAgent = async (proposal) => {
    const playerId = proposal?.player?.id;
    if (!playerId) return;
    setContactingId(playerId);
    const res = await trustedAgentService.contactAgent(id, playerId);
    setContactingId(null);
    if (res.success) {
      const chan = res.data?.channel?.channel_id;
      navigate(chan ? `/inbox?channel=${chan}` : "/inbox");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20 text-gray-500">Loading…</div>;
  }
  if (error || !delivery) {
    return (
      <div className="flex flex-col items-center py-20 text-center gap-3">
        <p className="text-red-500">{error || "No delivery yet."}</p>
        <button
          onClick={() => navigate(`/network/trusted-agent/${id}`)}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Back to the request
        </button>
      </div>
    );
  }

  const agent = delivery.agent || {};
  const count = delivery.proposal_count ?? delivery.proposals?.length ?? 0;
  const isEmpty = delivery.is_empty || count === 0;
  const expiry = chatExpiryLabel(delivery.delivered_at);
  const engagement = engagementLine(delivery, request);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate(`/network/trusted-agent/${id}`)}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#2283FF] mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to the request
        </button>

        {/* Header */}
        <div className="bg-white border border-[#D6DBE3] rounded-xl p-5 sm:p-6 flex flex-col gap-4 mb-5">
          <div className="flex items-start gap-3">
            <Avatar
              src={agent.avatar}
              fallbackText={agent.display_name || "Agent"}
              alt={agent.display_name || ""}
              size={44}
              className="flex-none"
            />
            <div className="flex flex-col min-w-0 flex-1">
              <h1 className="text-[19px] sm:text-[21px] font-bold text-[#111827] leading-tight">
                {isEmpty
                  ? "Nothing found"
                  : `${count} proposal${count === 1 ? "" : "s"} from ${
                      agent.display_name || "your agent"
                    }`}
              </h1>
              <span className="flex items-center gap-1.5 flex-wrap mt-0.5">
                {!isEmpty && (
                  <span className="text-[13px] text-gray-500">
                    from {agent.display_name}
                  </span>
                )}
                {isFifaVerified(agent) && <FifaVerifiedBadge size="sm" />}
                {agent.agency_name && (
                  <span className="text-[12px] text-gray-400">
                    · {agent.agency_name}
                  </span>
                )}
              </span>
              {delivery.delivered_at && (
                <span className="text-[12px] text-gray-500 mt-1">
                  Delivered {formatAbsoluteDate(delivery.delivered_at)}
                </span>
              )}
              {engagement.length > 0 && (
                <span className="text-[12px] text-gray-400 mt-0.5">
                  {engagement.map((bit, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && " · "}
                      {bit}
                    </React.Fragment>
                  ))}
                </span>
              )}
            </div>
          </div>

          {/* Chat chip + Extend / Open chat — wraps to its own line on mobile */}
          <div className="flex flex-wrap items-center gap-2 border-t border-[#F3F4F6] pt-3.5">
            {expiry && (
              <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#B45309] bg-[#FEF3C7] px-2.5 py-1 rounded-full">
                <Clock className="w-3.5 h-3.5" />
                {expiry}
              </span>
            )}
            <button
              type="button"
              onClick={openChat}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#111827] border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50"
            >
              <MessageSquare className="w-3.5 h-3.5 text-[#2283FF]" />
              Open chat &amp; extend
            </button>
          </div>
        </div>

        {/* Agent's summary note above the proposals */}
        {delivery.summary_note && (
          <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl p-4 sm:p-5 mb-5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {`A note from ${agent.display_name || "the agent"}`}
            </span>
            <p className="text-[13.5px] text-[#374151] leading-relaxed mt-1.5 break-words">
              {delivery.summary_note}
            </p>
          </div>
        )}

        {/* Body — empty path (A3) shows the reason; otherwise the A6·b cards */}
        {isEmpty ? (
          <div className="bg-white border border-[#D6DBE3] rounded-xl p-5 sm:p-6">
            <h2 className="text-[15px] font-bold text-[#111827] mb-2">
              The agent&apos;s reason
            </h2>
            <p className="text-[13.5px] text-[#374151] leading-relaxed break-words">
              {delivery.empty_reason || "No reason was given."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 min-[900px]:grid-cols-2 gap-4">
            {(delivery.proposals || []).map((p) => (
              <ProposalCard
                key={p.id}
                proposal={p}
                onContactAgent={contactAgent}
                contacting={contactingId === p.player?.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubDeliveryView;
